import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Chip,
  Stack,
  IconButton,
  LinearProgress,
  Button,
  Tooltip,
} from '@mui/material';
import { PlayArrow, Pause, Visibility, PlayCircleOutline, RestartAlt } from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { useContext } from 'react';
import { AuthContext } from '../../../contexts/user-context/user-context';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { DriverType } from '../../../interfaces/delivery-company/drivers';
import bearing from '@turf/bearing';
import along from '@turf/along';
import length from '@turf/length';

// Color palette for different duty sessions
const SESSION_COLORS = [
  '#1976d2', // Blue
  '#4CAF50', // Green
  '#FF9800', // Orange
  '#9C27B0', // Purple
  '#F44336', // Red
  '#00BCD4', // Cyan
  '#FFEB3B', // Yellow
  '#795548', // Brown
];

// Helper function to format time
const formatTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
};

// Helper function to calculate duration
const calculateDuration = (startTime: string, endTime: string | null): string => {
  if (!endTime) return 'Active';
  
  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffMs = end.getTime() - start.getTime();
  
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  }
  return `${minutes}min`;
};

interface DriverLocationMapProps {
  driver: DriverType;
}

interface LocationPoint {
  id: number;
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: string;
  activity_type?: string;
  battery_level?: number;
  is_moving: boolean;
}

interface LocationSession {
  session_id: number | null;
  start_time: string;
  end_time: string | null;
  locations: LocationPoint[];
}

interface LocationData {
  driver_id: string;
  driver_name: string;
  sessions: LocationSession[];
  total_locations: number;
}

const DriverLocationMap: React.FC<DriverLocationMapProps> = ({ driver }) => {
  const { config } = useContext(AuthContext);
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  
  // Animation state
  const [playingSessionId, setPlayingSessionId] = useState<number | null>(null);
  const [animationProgress, setAnimationProgress] = useState<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const isAnimatingRef = useRef<boolean>(false);
  const [motorcycleIconLoaded, setMotorcycleIconLoaded] = useState(false);
  const [focusedSessionId, setFocusedSessionId] = useState<number | null>(null);
  const [playAllActive, setPlayAllActive] = useState(false);

  // Initialize map - use a callback ref pattern for better reliability
  const initializeMap = () => {
    console.log('initializeMap called');
    console.log('mapContainer.current:', mapContainer.current);
    
    if (!mapContainer.current) {
      console.log('No map container ref');
      return;
    }
    
    if (map.current) {
      console.log('Map already initialized');
      return;
    }

    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    console.log('Mapbox token exists:', !!token);
    
    if (!token) {
      console.error('No Mapbox token found!');
      setError('Mapbox token is not configured');
      return;
    }

    mapboxgl.accessToken = token;

    console.log('Creating new Mapbox map...');
    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [35.9, 33.9], // Lebanon center
        zoom: 8,
      });

      console.log('Map created, adding controls...');
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      map.current.on('load', () => {
        console.log('✅ Map loaded successfully!');
        setMapLoaded(true);
      });

      map.current.on('error', (e) => {
        console.error('❌ Mapbox error:', e);
      });

    } catch (err) {
      console.error('❌ Error initializing map:', err);
      setError('Failed to initialize map: ' + (err as Error).message);
    }
  };

  // Track if we should show the map container
  const [showMap, setShowMap] = useState(false);

  // Helper functions to create GeoJSON Features with proper types
  const createPointFeature = (coordinates: [number, number], properties = {}) => ({
    type: 'Feature' as const,
    properties,
    geometry: { type: 'Point' as const, coordinates }
  });

  const createLineStringFeature = (coordinates: [number, number][], properties = {}) => ({
    type: 'Feature' as const,
    properties,
    geometry: { type: 'LineString' as const, coordinates }
  });

  // Load motorcycle icon (using same SVG as lebanon-map)
  const loadMotorcycleIcon = useCallback(async (mapInstance: mapboxgl.Map) => {
    // Ensure style is loaded
    if (!mapInstance.isStyleLoaded()) {
      console.log('Waiting for style to load before adding icon...');
      await new Promise<void>((resolve) => {
        mapInstance.once('idle', () => resolve());
      });
    }

    if (motorcycleIconLoaded || mapInstance.hasImage('icon-motorcycle')) {
      setMotorcycleIconLoaded(true);
      return;
    }

    const motorcycleSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
      <defs>
        <linearGradient id="mg" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:#ff9800"/>
          <stop offset="100%" style="stop-color:#e65100"/>
        </linearGradient>
        <filter id="ms" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
        </filter>
      </defs>
      <circle cx="24" cy="24" r="22" fill="url(#mg)" filter="url(#ms)"/>
      <circle cx="24" cy="24" r="20" fill="none" stroke="#fff" stroke-width="2" opacity="0.3"/>
      <rect x="8" y="18" width="22" height="14" rx="2" fill="#fff"/>
      <path d="M30 18h6l4 8v6h-10v-14z" fill="#e3f2fd"/>
      <rect x="32" y="20" width="6" height="6" rx="1" fill="#90caf9" stroke="#64b5f6" stroke-width="0.5"/>
      <circle cx="14" cy="32" r="4" fill="#424242"/>
      <circle cx="14" cy="32" r="2" fill="#757575"/>
      <circle cx="36" cy="32" r="4" fill="#424242"/>
      <circle cx="36" cy="32" r="2" fill="#757575"/>
      <rect x="10" y="22" width="8" height="6" rx="1" fill="#ffcc80"/>
    </svg>`;

    return new Promise<void>((resolve) => {
      const img = new Image(48, 48);
      img.onload = () => {
        if (!mapInstance.hasImage('icon-motorcycle')) {
          mapInstance.addImage('icon-motorcycle', img, { sdf: false, pixelRatio: 2 });
          console.log('Motorcycle icon loaded successfully');
        }
        setMotorcycleIconLoaded(true);
        resolve();
      };
      img.onerror = () => {
        console.error('Failed to load motorcycle icon');
        resolve();
      };
      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(motorcycleSvg)}`;
    });
  }, [motorcycleIconLoaded]);

  // Animation completion callback ref
  const animationCompleteCallback = useRef<(() => void) | null>(null);

  // Stop animation
  const stopAnimation = useCallback(() => {
    // Set flag immediately to stop any in-progress frames
    isAnimatingRef.current = false;
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // Small delay to ensure any in-flight frame completes
    setTimeout(() => {
      // Remove motorcycle from map
      if (map.current) {
        try {
          if (map.current.getLayer('motorcycle-icon')) {
            map.current.removeLayer('motorcycle-icon');
          }
        } catch (e) {
          console.warn('Error removing motorcycle layer:', e);
        }
        
        try {
          if (map.current.getSource('motorcycle')) {
            map.current.removeSource('motorcycle');
          }
        } catch (e) {
          console.warn('Error removing motorcycle source:', e);
        }
      }
      
      setPlayingSessionId(null);
      setAnimationProgress(0);
      
      // Call completion callback if set
      if (animationCompleteCallback.current) {
        const callback = animationCompleteCallback.current;
        animationCompleteCallback.current = null;
        callback();
      }
    }, 50);
  }, []);

  // Play animation for a session
  const playSession = useCallback(async (sessionIdx: number, onComplete?: () => void) => {
    if (!map.current || !locationData) {
      onComplete?.();
      return;
    }
    
    const session = locationData.sessions[sessionIdx];
    if (!session || session.locations.length < 2) {
      onComplete?.();
      return;
    }

    // Stop any existing animation
    stopAnimation();

    // Set completion callback
    if (onComplete) {
      animationCompleteCallback.current = onComplete;
    }

    // Wait for cleanup to complete
    await new Promise(resolve => setTimeout(resolve, 150));

    // Load motorcycle icon if needed
    await loadMotorcycleIcon(map.current);

    setPlayingSessionId(sessionIdx);
    setAnimationProgress(0);
    isAnimatingRef.current = true;

    const coords = session.locations.map(loc => [loc.longitude, loc.latitude]) as [number, number][];
    
    // Validate coordinates
    if (!coords || coords.length < 2) {
      console.error('Invalid coordinates for session');
      stopAnimation();
      return;
    }

    const routeLine = createLineStringFeature(coords);
    let routeLength: number;
    
    try {
      routeLength = length(routeLine, { units: 'kilometers' });
      if (!routeLength || routeLength <= 0) {
        console.error('Invalid route length');
        stopAnimation();
        return;
      }
    } catch (e) {
      console.error('Error calculating route length:', e);
      stopAnimation();
      return;
    }

    // Add motorcycle source
    try {
      if (!map.current.getSource('motorcycle')) {
        map.current.addSource('motorcycle', {
          type: 'geojson',
          data: createPointFeature(coords[0]) as any
        });
      }

      // Add motorcycle layer
      if (!map.current.getLayer('motorcycle-icon')) {
        map.current.addLayer({
          id: 'motorcycle-icon',
          type: 'symbol',
          source: 'motorcycle',
          layout: {
            'icon-image': 'icon-motorcycle',
            'icon-size': 1.2,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-anchor': 'center'
          }
        });
      }
    } catch (e) {
      console.error('Error setting up motorcycle layer:', e);
      stopAnimation();
      return;
    }

    // Animation variables
    const animationDuration = 10000; // 10 seconds total
    const startTime = performance.now();

    const animateMotorcycle = (currentTime: number) => {
      // Check if animation should stop
      if (!isAnimatingRef.current) {
        return;
      }

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);
      
      setAnimationProgress(progress * 100);

      if (progress >= 1) {
        stopAnimation();
        return;
      }

      // Calculate current position with safety checks
      try {
        const distanceTraveled = progress * routeLength;
        
        if (distanceTraveled > routeLength || distanceTraveled < 0) {
          console.error('Invalid distance traveled');
          stopAnimation();
          return;
        }

        const currentPoint = along(routeLine, distanceTraveled, { units: 'kilometers' });
        
        if (!currentPoint?.geometry?.coordinates || 
            !Array.isArray(currentPoint.geometry.coordinates) ||
            currentPoint.geometry.coordinates.length !== 2) {
          console.error('Invalid currentPoint from along calculation');
          stopAnimation();
          return;
        }
        
        const currentCoords = currentPoint.geometry.coordinates as [number, number];

        // Update motorcycle position
        const motorcycleSource = map.current?.getSource('motorcycle') as mapboxgl.GeoJSONSource;
        if (motorcycleSource && isAnimatingRef.current) {
          motorcycleSource.setData(createPointFeature(currentCoords) as any);
        }

        // Continue animation only if still active
        if (isAnimatingRef.current) {
          animationFrameRef.current = requestAnimationFrame(animateMotorcycle);
        }
      } catch (e) {
        console.error('Error during animation frame:', e);
        stopAnimation();
      }
    };

    animationFrameRef.current = requestAnimationFrame(animateMotorcycle);
  }, [locationData, stopAnimation, loadMotorcycleIcon]);

  // Pause animation
  const pauseSession = useCallback(() => {
    stopAnimation();
    setPlayAllActive(false);
  }, [stopAnimation]);

  // Focus on a specific session
  const focusSession = useCallback((sessionIdx: number) => {
    if (!map.current || !locationData) return;
    
    const session = locationData.sessions[sessionIdx];
    if (!session || session.locations.length === 0) return;

    const coords = session.locations.map(loc => [loc.longitude, loc.latitude]) as [number, number][];
    const bounds = new mapboxgl.LngLatBounds();
    coords.forEach(coord => bounds.extend(coord));
    
    map.current.fitBounds(bounds, { padding: 80, duration: 1000 });
    setFocusedSessionId(sessionIdx);
  }, [locationData]);

  // Reset focus to show all sessions
  const resetFocus = useCallback(() => {
    if (!map.current || !locationData) return;

    const bounds = new mapboxgl.LngLatBounds();
    locationData.sessions.forEach(session => {
      session.locations.forEach(loc => {
        bounds.extend([loc.longitude, loc.latitude]);
      });
    });

    if (!bounds.isEmpty()) {
      map.current.fitBounds(bounds, { padding: 50, duration: 1000 });
    }
    setFocusedSessionId(null);
  }, [locationData]);

  // Play all sessions sequentially
  const playAllSessions = useCallback(async () => {
    if (!locationData || locationData.sessions.length === 0) return;
    
    setPlayAllActive(true);
    
    for (let i = 0; i < locationData.sessions.length; i++) {
      const session = locationData.sessions[i];
      if (session.locations.length < 2) continue;
      
      // Focus on this session
      focusSession(i);
      
      // Wait a bit for focus animation
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Play the session and wait for completion
      await new Promise<void>((resolve) => {
        playSession(i, resolve);
      });
      
      // Small pause between sessions
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    // Reset focus after all sessions
    resetFocus();
    setPlayAllActive(false);
  }, [locationData, playSession, focusSession, resetFocus]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Initialize map once when we first get location data
  useEffect(() => {
    // Show map container once we have location data for the first time
    if (locationData && locationData.sessions.length > 0 && !showMap) {
      const hasLocations = locationData.sessions.some(session => session.locations.length > 0);
      if (hasLocations) {
        setShowMap(true);
      }
    }
  }, [locationData, showMap]);

  // Initialize map when container becomes available
  useEffect(() => {
    if (!showMap) {
      return;
    }

    // If map is already initialized, don't reinitialize
    if (map.current) {
      return;
    }

    console.log('Map initialization effect running');
    
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      initializeMap();
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [showMap]);

  // Cleanup map on unmount
  useEffect(() => {
    return () => {
      if (map.current) {
        console.log('Cleaning up map on unmount...');
        map.current.remove();
        map.current = null;
        setMapLoaded(false);
      }
    };
  }, []);

  // Fetch location data
  useEffect(() => {
    const fetchLocations = async () => {
      if (!driver.id) return;

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get<LocationData>(
          `${API_ENDPOINT}/api/v1/drivers/${driver.id}/locations/`,
          {
            ...config,
            params: { date: selectedDate },
          }
        );

        setLocationData(response.data);
      } catch (err) {
        setError('Failed to load location data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, [driver.id, selectedDate, config]);

  // Update map with location data
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      console.log('Map update skipped - map not ready:', { 
        hasMap: !!map.current, 
        mapLoaded
      });
      return;
    }

    // Always clear existing markers and routes first
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Remove all existing route layers and sources
    if (locationData?.sessions) {
      locationData.sessions.forEach((_, idx) => {
        const layerId = `route-${idx}`;
        const sourceId = `route-${idx}`;
        if (map.current!.getLayer(layerId)) {
          map.current!.removeLayer(layerId);
        }
        if (map.current!.getSource(sourceId)) {
          map.current!.removeSource(sourceId);
        }
      });
    }

    // If no location data, just return after clearing
    if (!locationData || locationData.sessions.length === 0) {
      console.log('No location data - map cleared');
      // Reset map view to default
      map.current.flyTo({
        center: [35.9, 33.9], // Lebanon center
        zoom: 8,
      });
      return;
    }

    console.log('Updating map with', locationData.sessions.length, 'session(s)');

    const bounds = new mapboxgl.LngLatBounds();

    // Render each session with its own color
    locationData.sessions.forEach((session, sessionIdx) => {
      if (session.locations.length === 0) return;

      const color = SESSION_COLORS[sessionIdx % SESSION_COLORS.length];
      const locations = session.locations;
      const coordinates = locations.map(loc => [loc.longitude, loc.latitude]);

      // Create route line for this session
      const sourceId = `route-${sessionIdx}`;
      const layerId = `route-${sessionIdx}`;

      map.current!.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: coordinates,
          },
        },
      });

      map.current!.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': color,
          'line-width': 3,
        },
      });

      // Add start marker for this session
      const startMarker = new mapboxgl.Marker({ color: color })
        .setLngLat([locations[0].longitude, locations[0].latitude])
        .setPopup(
          new mapboxgl.Popup().setHTML(
            `<strong>Session ${sessionIdx + 1} Start</strong><br/>${new Date(locations[0].timestamp).toLocaleString()}`
          )
        )
        .addTo(map.current!);
      markersRef.current.push(startMarker);

      // Add end marker for this session if it has multiple locations
      if (locations.length > 1) {
        const lastLocation = locations[locations.length - 1];
        const endMarker = new mapboxgl.Marker({ color: color })
          .setLngLat([lastLocation.longitude, lastLocation.latitude])
          .setPopup(
            new mapboxgl.Popup().setHTML(
              `<strong>Session ${sessionIdx + 1} End</strong><br/>${new Date(lastLocation.timestamp).toLocaleString()}`
            )
          )
          .addTo(map.current!);
        markersRef.current.push(endMarker);
      }

      // Extend bounds to include all locations from this session
      coordinates.forEach(coord => bounds.extend(coord as [number, number]));
    });

    // Fit map to show all routes
    if (!bounds.isEmpty()) {
      map.current.fitBounds(bounds, { padding: 50 });
    }
  }, [locationData, mapLoaded]);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              Driver Location History
            </Typography>
            {driver.is_on_duty && (
              <Chip label="Currently On Duty" color="success" size="small" />
            )}
          </Box>

          <TextField
            type="date"
            label="Select Date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ maxWidth: 200 }}
          />

          {loading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error">{error}</Alert>
          )}

          {!loading && locationData && locationData.sessions.length === 0 && !showMap && (
            <Alert severity="info">
              No location data available for {selectedDate}. The driver may not have been on duty or tracking may not have been enabled.
            </Alert>
          )}

          {showMap && (
            <>
              {/* Summary chips */}
              {!loading && locationData && locationData.sessions.length > 0 && (
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Chip
                    label={`${locationData.total_locations} location points`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`${locationData.sessions.length} session(s)`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                </Box>
              )}

              {/* Map and Sessions Side by Side */}
              <Box 
                display="flex" 
                gap={2}
                sx={{
                  flexDirection: { xs: 'column', md: 'row' },
                  height: { xs: 'auto', md: '500px' },
                }}
              >
                {/* Map Container */}
                <Box
                  ref={mapContainer}
                  sx={{
                    flex: { xs: '1 1 auto', md: '0 0 68%' },
                    height: { xs: '400px', md: '100%' },
                    minHeight: '400px',
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: '#f0f0f0',
                    position: 'relative',
                    '& .mapboxgl-map': {
                      width: '100%',
                      height: '100%',
                    }
                  }}
                />

                {/* Sessions List - Scrollable on the right */}
                {!loading && locationData && locationData.sessions.length > 0 && (
                  <Box
                    sx={{
                      flex: { xs: '1 1 auto', md: '0 0 30%' },
                      height: { xs: 'auto', md: '100%' },
                      maxHeight: { xs: '300px', md: '500px' },
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <Box sx={{ p: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
                        On-Duty Sessions
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PlayCircleOutline />}
                          onClick={playAllSessions}
                          disabled={playAllActive || playingSessionId !== null}
                          fullWidth
                        >
                          Play All
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<RestartAlt />}
                          onClick={resetFocus}
                          disabled={focusedSessionId === null && playingSessionId === null}
                          fullWidth
                        >
                          Reset
                        </Button>
                      </Stack>
                    </Box>
                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                      <Stack spacing={1}>
                        {locationData.sessions.map((session, idx) => {
                          const isPlaying = playingSessionId === idx;
                          const isOtherPlaying = playingSessionId !== null && playingSessionId !== idx;
                          const isFocused = focusedSessionId === idx;
                          const canPlay = session.locations.length >= 2;
                          
                          return (
                            <Box 
                              key={session.session_id || `legacy-${idx}`} 
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: isPlaying || isFocused ? SESSION_COLORS[idx % SESSION_COLORS.length] : 'divider',
                                backgroundColor: 'background.default',
                                transition: 'all 0.2s',
                                opacity: isOtherPlaying ? 0.6 : 1,
                                '&:hover': {
                                  borderColor: SESSION_COLORS[idx % SESSION_COLORS.length],
                                  boxShadow: 1,
                                }
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                <Box 
                                  sx={{ 
                                    width: 20, 
                                    height: 3, 
                                    bgcolor: SESSION_COLORS[idx % SESSION_COLORS.length],
                                    borderRadius: 0.5,
                                    flexShrink: 0,
                                  }} 
                                />
                                <Typography variant="caption" fontWeight={600}>
                                  Session {idx + 1}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', mr: 0.5 }}>
                                  {session.locations.length} pts
                                </Typography>
                                <Tooltip title="View this session">
                                  <IconButton
                                    size="small"
                                    onClick={() => focusSession(idx)}
                                    disabled={playAllActive}
                                    sx={{
                                      p: 0.3,
                                      color: isFocused ? SESSION_COLORS[idx % SESSION_COLORS.length] : 'text.secondary',
                                      '&:hover': {
                                        bgcolor: `${SESSION_COLORS[idx % SESSION_COLORS.length]}22`,
                                        color: SESSION_COLORS[idx % SESSION_COLORS.length],
                                      }
                                    }}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={isPlaying ? "Pause" : "Play"}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => isPlaying ? pauseSession() : playSession(idx)}
                                      disabled={(isOtherPlaying && !isPlaying) || !canPlay || playAllActive}
                                      sx={{
                                        p: 0.3,
                                        border: '1px solid',
                                        borderColor: isPlaying ? SESSION_COLORS[idx % SESSION_COLORS.length] : 'divider',
                                        color: SESSION_COLORS[idx % SESSION_COLORS.length],
                                        '&:hover': {
                                          bgcolor: `${SESSION_COLORS[idx % SESSION_COLORS.length]}22`,
                                          borderColor: SESSION_COLORS[idx % SESSION_COLORS.length],
                                        },
                                        '&.Mui-disabled': {
                                          opacity: 0.4,
                                          borderColor: 'divider',
                                        }
                                      }}
                                    >
                                      {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            
                            {/* Progress bar when playing */}
                            {isPlaying && (
                              <Box sx={{ mb: 0.5 }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={animationProgress} 
                                  sx={{
                                    height: 3,
                                    borderRadius: 1,
                                    backgroundColor: 'divider',
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: SESSION_COLORS[idx % SESSION_COLORS.length],
                                    }
                                  }}
                                />
                              </Box>
                            )}
                            
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                                {formatTime(session.start_time)} → {session.end_time ? formatTime(session.end_time) : ''}
                              </Typography>
                              {!session.end_time && (
                                <Chip 
                                  label="Active" 
                                  size="small" 
                                  color="success"
                                  sx={{ 
                                    height: 16,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    '& .MuiChip-label': { px: 0.75, py: 0 }
                                  }}
                                />
                              )}
                            </Box>
                            {session.end_time && (
                              <Typography variant="caption" display="block" fontWeight={500} sx={{ lineHeight: 1.3 }}>
                                {calculateDuration(session.start_time, session.end_time)}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                    </Box>
                  </Box>
                )}
              </Box>

              {!loading && locationData && locationData.sessions.length === 0 && (
                <Alert severity="info">
                  No location data available for {selectedDate}. The driver may not have been on duty or tracking may not have been enabled.
                </Alert>
              )}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DriverLocationMap;

